import { CategoryPageProps } from 'models/CategoryPageProps';
import { AccountPageProps } from 'models/AccountPageProps';
import { GetServerSidePropsContext } from 'next';
import { ShopApiRequestParams } from 'models/ShopApiRequestParams';
import { transformQueryParamsToApiParams } from './transformQueryParamsToApiParams';
import { getShopsFromApi } from 'helpers/getShopsFromApi';
import { ShopSearchRawQueryParams } from 'models/ShopSearchRawQueryParams';
import { nextQueryFirstString } from './normalizeShopSearchRawQuery';
import { getActiveCategoryByCategoryPath } from './getActiveCategoryByCategoryPath';
import { Environment } from 'models/Environment';
import { TargetMarket } from 'models/TargetMarket';
import { SEARCH_TERM_MAX_LENGTH, SEARCH_TERM_MIN_LENGTH } from './constants';
import { STATUS_CODES } from './statusCodes';

export const getSearchResults = async (
  context: GetServerSidePropsContext,
  environment: Environment,
  targetMarket: TargetMarket
): Promise<CategoryPageProps | AccountPageProps> => {
  const {
    category,
    searchTerm,
    keyword,
    account,
    sort,
    hasValidCertificate,
    page
  }: ShopApiRequestParams = transformQueryParamsToApiParams(context);

  if (
    searchTerm.length > 0 &&
    (searchTerm.length < SEARCH_TERM_MIN_LENGTH ||
      searchTerm.length > SEARCH_TERM_MAX_LENGTH)
  ) {
    /* istanbul ignore else */
    if (typeof window === 'undefined') {
      context.res.statusCode = STATUS_CODES.UNPROCESSABLE_ENTITY;
    }
    throw STATUS_CODES.UNPROCESSABLE_ENTITY;
  }

  const { metaData, shops, categories } = await getShopsFromApi({
    category,
    searchTerm,
    keyword,
    account,
    sort,
    hasValidCertificate,
    page,
    targetMarket,
    environment
  }).catch((errorStatusCode) => {
    /* istanbul ignore next */
    if (context && context.res) {
      context.res.statusCode = errorStatusCode;
    }
    throw errorStatusCode;
  });

  const safeMetaData = metaData || { totalPageCount: 0, totalShopCount: 0 };

  if (safeMetaData.totalShopCount === 0) {
    if (context && context.res) {
      context.res.statusCode = STATUS_CODES.GONE;
    }
  }

  // TODO: This came in with SEO-1675 and should get removed with SEO-1676
  /* istanbul ignore if */
  if (shops) {
    const farmaone = shops.find(
      ({ tsID }) => tsID === 'X4E251A928880DDA0AF25C02D6609F7B6'
    );
    if (farmaone) {
      farmaone.shopUrl = '';
    }
  }

  const rawQuery = context.query as ShopSearchRawQueryParams;
  const categoryPath = nextQueryFirstString(rawQuery.category);

  const activatedCategory =
    (categoryPath &&
      getActiveCategoryByCategoryPath(categoryPath, categories)) ||
    null;

  const pageStr = nextQueryFirstString(rawQuery.page);
  /** Invalid `page` is rejected earlier in {@link transformQueryParamsToApiParams}. */
  const activePage = pageStr ? parseInt(pageStr, 10) : 1;

  const safeCategoryList =
    categories && categories.length > 0 ? categories : [];

  return {
    shops: shops || [],
    metadata: safeMetaData,
    queryParams: rawQuery,
    category: activatedCategory,
    account,
    searchTerm,
    sort,
    hasValidCertificate: !!hasValidCertificate,
    activePage,
    categoryList: safeCategoryList
  };
};
